import { styled } from "styled-components";

import { Alert, Pill, Radio, Spacer, Text } from "@vericus/cadmus-ui";

import { TaskFormat } from "generated/graphql";

import { SettingInfoCard } from "../setting-info-card";

interface Props {
  gradingService: string;
  format: TaskFormat;
  setGradingService: (service: string) => void;
  hasMoodleGrader?: boolean;
  hasTurnitinGrader?: boolean;
  hasSpeedGrader?: boolean;
  hasCadmusGrader?: boolean;
}

/**
 * Radio group selector for the Marking Tool.
 */
export function MarkingToolRadio(props: Props) {
  const {
    hasMoodleGrader = false,
    hasTurnitinGrader = true,
    hasSpeedGrader = false,
    hasCadmusGrader = false,
    format,
  } = props;

  const noGrader =
    !hasMoodleGrader &&
    !hasTurnitinGrader &&
    !hasSpeedGrader &&
    !hasCadmusGrader;

  return (
    <FieldSet>
      <legend>
        <Text kind="headingFour" as="span">
          Marking Tool
        </Text>
      </legend>
      <Spacer spacing={20} />
      {hasCadmusGrader && (
        <>
          <Radio
            name="gradingService"
            content="Use Cadmus Marker"
            value="cadmusgrader"
            colorVariant="lilac500"
            checked={props.gradingService === "cadmusgrader"}
            onChange={() => props.setGradingService("cadmusgrader")}
          />

          <Spacer spacing={16} />
          <SettingInfoCard showNewPill={false} backgroundColor="blue">
            <Pill>Recommended</Pill>
            <Text
              kind="bodyLine"
              style={{ margin: "8px 0", display: "inline-block" }}
            >
              {format === TaskFormat.Multiformat
                ? "Annotate and mark with rubrics, auto-mark questions with advanced moderation features, and selectively release feedback."
                : "Annotate and mark with rubrics, access advanced moderation tools for quality control, and selectively release feedback."}
            </Text>
            <Alert severity="warning" size="sm">
              You&apos;ll need to release grades and feedback manually through
              Cadmus.
            </Alert>
          </SettingInfoCard>

          <Spacer spacing={16} />
        </>
      )}
      {noGrader && (
        <Radio
          name="gradingService"
          colorVariant="lilac500"
          content="Use Turnitin Feedback Studio for marking"
          value="turnitin"
          defaultChecked={props.gradingService === "turnitin"}
          disabled
          readOnly
        />
      )}
      {hasTurnitinGrader && (
        <Radio
          name="gradingService"
          colorVariant="lilac500"
          content="Use Turnitin Feedback Studio for marking"
          value="turnitin"
          checked={props.gradingService === "turnitin"}
          onChange={() => props.setGradingService("turnitin")}
        />
      )}
      {hasMoodleGrader && (
        <Radio
          name="gradingService"
          colorVariant="lilac500"
          content="Use Moodle Grader for marking"
          value="moodlegrader"
          checked={props.gradingService === "moodlegrader"}
          onChange={() => props.setGradingService("moodlegrader")}
        />
      )}
      {hasSpeedGrader && (
        <Radio
          name="gradingService"
          colorVariant="lilac500"
          content="Use Canvas SpeedGrader for marking"
          value="speedgrader"
          checked={props.gradingService === "speedgrader"}
          onChange={() => props.setGradingService("speedgrader")}
        />
      )}
    </FieldSet>
  );
}

const FieldSet = styled.fieldset`
  border: none;
  padding: 0px;
  display: flex;
  flex-direction: column;
`;
